import React, { useState, useEffect } from 'react';
import './EligibilityCards.css';
import { FaCheckCircle, FaTimesCircle, FaFolderOpen } from 'react-icons/fa';
import collegeConfig from '../../config/collegeConfig';

// Card-based exam eligibility results — summary bar + one card per student.
// Shared by the HOD's "Monitor Department" view (TeacherDashboard) and the
// Monitor / Department Monitor dashboards.
const EligibilityCards = ({ eligibilityList, onViewDocuments }) => {
  const [batchLabels, setBatchLabels] = useState({});

  useEffect(() => {
    fetch(`${collegeConfig.api.baseURL}/system-settings`)
      .then((r) => r.json())
      .then((d) => {
        if (d.success && Array.isArray(d.batchLabels)) {
          setBatchLabels(Object.fromEntries(d.batchLabels.map((b) => [b.batch, b.label])));
        }
      })
      .catch(() => {});
  }, []);

  if (eligibilityList.length === 0) {
    return <div className="elig-empty"><p>No students found.</p></div>;
  }

  return (
    <div>
      <div className="elig-summary">
        <span className="elig-stat eligible">
          <FaCheckCircle /> {eligibilityList.filter(e => e.overallEligible).length} Eligible
        </span>
        <span className="elig-stat not-eligible">
          <FaTimesCircle /> {eligibilityList.filter(e => !e.overallEligible).length} Not Eligible
        </span>
        <span className="elig-stat total">
          {eligibilityList.length} Total
        </span>
      </div>

      <div className="elig-cards">
        {eligibilityList.map(item => {
          const { student, checks, overallEligible } = item;
          const checkItems = [
            ...(checks.weeklyLogbook.applicable !== false
              ? [{ label: 'Weekly Log', passed: checks.weeklyLogbook.passed, sub: `${checks.weeklyLogbook.submittedWeeks}/12 wks` }]
              : []),
            { label: 'DRP', passed: checks.drpCompletion.passed },
            { label: 'Res. Meth.', passed: checks.certificates.researchMethodology.passed },
            { label: 'Ethics', passed: checks.certificates.ethicsCourse.passed },
            { label: 'BCLS/ACLS', passed: checks.certificates.bclsAcls.passed },
            { label: 'Dissertation', passed: checks.dissertation.passed, sub: `${checks.dissertation.gradedPeriods}/6` },
            { label: 'Publication', passed: checks.publications.passed, sub: checks.publications.count > 0 ? `${checks.publications.count}` : null },
            { label: 'Seminars', passed: checks.seminarsActivities.passed, na: !checks.seminarsActivities.applicable },
            { label: 'UG Teaching', passed: checks.ugTeaching.passed, na: !checks.ugTeaching.applicable }
          ];
          return (
            <div key={student.id} className={`elig-card ${overallEligible ? 'elig-card-pass' : 'elig-card-fail'}`}>
              <div className="elig-card-header">
                <div className="elig-card-student">
                  <span className="elig-card-name">{student.name}</span>
                  <div className="elig-card-meta">
                    {student.registrationNumber && <span>{student.registrationNumber}</span>}
                    {student.batch && <span>{batchLabels[student.batch] || student.batch}</span>}
                    {student.department && <span>{student.department}</span>}
                  </div>
                </div>
                {overallEligible
                  ? <span className="elig-badge pass">Eligible</span>
                  : <span className="elig-badge fail">Pending</span>
                }
              </div>

              <div className="elig-card-checks">
                {checkItems.map((c, i) => (
                  <div key={i} className={`elig-chip ${c.na ? 'na' : c.passed ? 'pass' : 'fail'}`} title={c.label}>
                    {c.na
                      ? <span className="elig-chip-icon">—</span>
                      : c.passed
                        ? <FaCheckCircle className="elig-chip-icon" />
                        : <FaTimesCircle className="elig-chip-icon" />
                    }
                    <span className="elig-chip-label">{c.label}</span>
                    {c.sub && <span className="elig-chip-sub">{c.sub}</span>}
                  </div>
                ))}
              </div>

              {onViewDocuments && (
                <div className="elig-card-footer">
                  <button
                    className="elig-docs-btn"
                    onClick={() => onViewDocuments(student)}
                    title="View / download supporting documents"
                  >
                    <FaFolderOpen /> Documents
                  </button>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default EligibilityCards;
